import { Card } from "design-system/molecules/Card"
import {
  ComparisonActivityChart,
  ComparisonChartConfig,
} from "design-system/organisms/insights/ComparisonActivityChart"
import { useTranslation } from "react-i18next"
import {
  BackofficeSupportTreemapDomainEnum,
  ClusterDomainEnum,
  GetDeflectionActivityResponseDataSchema,
} from "schema"
import { z } from "zod"

type Domain = (typeof BackofficeSupportTreemapDomainEnum)[number]

type Props = {
  domain: Exclude<Domain, typeof ClusterDomainEnum.Support>
  data: z.infer<typeof GetDeflectionActivityResponseDataSchema> | undefined
  isPending: boolean
}

type DomainChartConfig = {
  baseKey: string
  topKey: string
  rateKey: string
  chartConfig: ComparisonChartConfig
}

export function BackofficeSupportDomainActivity({ domain, data, isPending }: Props) {
  const { t } = useTranslation()

  const config = getChartConfig(domain, t)
  const chartData = data?.dailyCount.every(({ baseValue }) => baseValue === 0)
    ? []
    : data?.dailyCount.map((day) => ({
        date: day.date,
        [config.baseKey]: day.baseValue,
        [config.topKey]: day.topValue,
        [config.rateKey]: day.rate !== undefined ? Math.round(day.rate * 100) : undefined,
      }))

  return (
    <Card className="p-4">
      <ComparisonActivityChart
        data={chartData}
        chartConfig={config.chartConfig}
        chartClassName="h-[140px]"
        isPending={isPending}
        noDataT={{
          title: t("dashboard.activity.noData.title"),
          description: t("dashboard.activity.noData.description"),
        }}
      />
    </Card>
  )
}

function getChartConfig(
  domain: Props["domain"],
  t: ReturnType<typeof useTranslation>["t"],
): DomainChartConfig {
  switch (domain) {
    case ClusterDomainEnum.Issue:
      return {
        baseKey: "inbound",
        topKey: "withoutIssue",
        rateKey: "deflectionRate",
        chartConfig: {
          inbound: {
            label: t("dashboard.activity.inbound"),
            color: "var(--chart-secondary)",
            type: "bar",
          },
          withoutIssue: {
            label: t("dashboard.activity.withoutIssue"),
            color: "var(--chart-primary)",
            type: "bar",
          },
          deflectionRate: {
            label: t("dashboard.metrics.issueDeflectionRate.title"),
            color: "var(--success)",
            type: "line",
          },
        } satisfies ComparisonChartConfig,
      }
    case ClusterDomainEnum.Question:
      return {
        baseKey: "questions",
        topKey: "answered",
        rateKey: "autoAnswerRate",
        chartConfig: {
          questions: {
            label: t("dashboard.activity.questions"),
            color: "color-mix(in srgb, var(--chart-4) 25%, transparent)",
            type: "bar",
          },
          answered: {
            label: t("dashboard.tooltip.activity.autoAnswered"),
            color: "var(--chart-4)",
            type: "bar",
          },
          autoAnswerRate: {
            label: t("dashboard.tooltip.activity.autoAnswerRate"),
            color: "var(--success)",
            type: "line",
          },
        } satisfies ComparisonChartConfig,
      }
    case ClusterDomainEnum.ActionRequest:
      return {
        baseKey: "requests",
        topKey: "fulfilled",
        rateKey: "fulfillmentRate",
        chartConfig: {
          requests: {
            label: t("dashboard.tooltip.activity.requests"),
            color: "var(--chart-2)",
            type: "bar",
          },
          fulfilled: {
            label: t("dashboard.tooltip.activity.fulfilled"),
            color: "var(--chart-3)",
            type: "bar",
          },
          fulfillmentRate: {
            label: t("dashboard.activity.autoFulfillmentRate"),
            color: "var(--success)",
            type: "line",
          },
        } satisfies ComparisonChartConfig,
      }
  }
}
